<template>
	<div class="flex flex-col gap-4 p-4">
		<!-- Validation running indicator -->
		<div v-show="displayValidationRunning">Running Validation Check...</div>

		<!-- Per-option validation warnings -->
		<div v-if="optionValidationResults.length > 0">
			<warningBlock header="Warning" :warningMessages="flattenedValidationMessages" />
		</div>

		<!-- Preview validation warning (missing required fields) -->
		<div v-if="showPreviewValidationWarning && previewValidationMessages.length > 0">
			<warningBlock header="Cannot Preview" :warningMessages="previewValidationMessages" />
		</div>

		<!-- Loading overlay -->
		<div v-if="loading" class="flex items-center justify-center py-8">
			<i class="pi pi-spin pi-spinner text-2xl text-gray-400 mr-2"></i>
			<span class="text-gray-500">Loading dashboard configuration...</span>
		</div>

		<!-- PREVIEW MODE -->
		<template v-else-if="showPreview">
			<!-- Preview header -->
			<div class="flex items-center justify-between mb-2">
				<h2 class="text-xl font-semibold text-gray-700">Dashboard Preview</h2>
				<div class="flex gap-2">
					<Button label="Back to Editor" icon="pi pi-arrow-left" severity="info" @click="showPreview = false" />
					<Button
						label="Export PPT"
						icon="pi pi-download"
						severity="secondary"
						:loading="exporting"
						:disabled="!canExport"
						@click="exportPowerPoint"
					/>
					<Button label="Export PDF" icon="pi pi-file-pdf" severity="secondary" :loading="exportingPdf" :disabled="!canExport" @click="exportPdf" />
					<!-- <Button label="PPT VIVA!" icon="pi pi-star" severity="warn" :loading="exportingViva" :disabled="!canExport" @click="showVivaDialog = true" /> -->
				</div>
			</div>

			<!-- Preview loading -->
			<div v-if="loadingPreview" class="flex items-center justify-center py-12">
				<i class="pi pi-spin pi-spinner text-2xl text-gray-400 mr-2"></i>
				<span class="text-gray-500">Generating preview...</span>
			</div>

			<!-- Preview content - Page 1 -->
			<div v-else-if="previewData" class="preview-container bg-white border border-gray-300 rounded-lg shadow-md p-6">
				<!-- Row 1: Metadata | Reduction Highlights | Radar Chart -->
				<div class="grid grid-cols-1 xl:grid-cols-[3fr_2fr_4fr] gap-6 mb-6">
					<!-- LEFT: Metadata table -->
					<div class="preview-metadata">
						<table class="w-full border-collapse text-sm">
							<tbody>
								<tr>
									<td class="preview-header-cell">Assessment Name</td>
									<td class="preview-value-cell">{{ previewData.assessmentName }}</td>
								</tr>
								<tr>
									<td class="preview-header-cell">Description</td>
									<td class="preview-value-cell">{{ previewData.description || "-" }}</td>
								</tr>
								<tr>
									<td class="preview-header-cell">Baseline Format</td>
									<td class="preview-value-cell">{{ previewData.baselineFormatName }}</td>
								</tr>
								<tr v-for="alt in previewData.alternatives" :key="alt.label">
									<td class="preview-header-cell">{{ alt.label }}</td>
									<td class="preview-value-cell">{{ alt.formatName }}</td>
								</tr>
								<tr>
									<td class="preview-header-cell">Date</td>
									<td class="preview-value-cell">{{ previewData.date }}</td>
								</tr>
								<tr>
									<td class="preview-header-cell">Contact Person</td>
									<td class="preview-value-cell">{{ previewData.contactPerson }}</td>
								</tr>
							</tbody>
						</table>
					</div>

					<!-- CENTER: Reduction Highlights -->
					<div class="preview-reductions flex items-center justify-center">
						<div class="flex flex-col gap-4">
							<div v-for="rh in previewData.reductionHighlights" :key="rh.label" class="flex items-center gap-3">
								<span v-tooltip.right="getKpiTooltip(rh)" class="reduction-icon" style="cursor: help">
									<FontAwesomeIcon :icon="['fas', kpiIconMap[rh.icon] || 'circle-info']" class="reduction-fa-icon" />
								</span>
								<div class="flex flex-col">
									<span class="text-xl font-bold text-gray-800">
										{{ formatReductionValue(rh.value, rh.icon) }}
									</span>
									<span class="text-xs text-gray-600 leading-tight">{{ rh.label.toLowerCase() }}</span>
								</div>
							</div>
							<div v-if="!previewData.reductionHighlights?.length" class="text-gray-400 text-sm italic">No reduction highlights available</div>
						</div>
					</div>

					<!-- RIGHT: Radar Chart -->
					<div class="preview-radar" style="height: 360px; position: relative; overflow: hidden">
						<dashboardSpiderChart
							v-if="previewData.radarChart && previewData.radarChart.labels?.length >= 3"
							:labels="previewData.radarChart.labels"
							:datasets="previewRadarDatasets"
							:axisMin="previewData.radarChart.axisMin"
							:axisMax="previewData.radarChart.axisMax"
						/>
						<div v-else class="flex items-center justify-center h-full text-gray-400 text-sm italic">
							Radar chart requires at least 3 key indicators
						</div>
						<button
							v-if="previewData.radarChart && previewData.radarChart.labels?.length >= 3"
							v-tooltip.left="'Expand Radar Chart'"
							class="radar-expand-btn"
							@click="showRadarDialog = true"
						>
							<i class="pi pi-expand"></i>
						</button>
					</div>
				</div>

				<!-- Dashboard Configuration Summary -->
				<div class="mb-6">
					<h3 class="text-sm font-semibold text-gray-700 mb-2">Package Formats</h3>
					<DataTable :value="dashboardConfigSummary" size="small" class="preview-datatable">
						<Column field="label" header="" />
						<Column field="packagingOption">
							<template #header><I2Label label="Packaging Option" helpElementName="AssessmentTabDashboardPackagingOption" /></template>
						</Column>
						<Column field="distributionOption">
							<template #header><I2Label label="Distribution Option" helpElementName="AssessmentTabDashboardDistributionOption" /></template>
						</Column>
						<Column field="amount">
							<template #header><I2Label label="Amount" helpElementName="AssessmentTabDashboardAmount" /></template>
							<template #body="{ data }">{{ typeof data.amount === "number" ? formatNumber(data.amount) : data.amount }}</template>
						</Column>
						<Column field="scaling">
							<template #header><I2Label label="Scaling" helpElementName="AssessmentTabDashboardScaling" /></template>
						</Column>
						<Column field="reductionFocus">
							<template #header><I2Label label="Reduction Focus" helpElementName="AssessmentTabDashboardReductionFocus" /></template>
						</Column>
					</DataTable>
				</div>

				<!-- Row 2: Relatable Units -->
				<div v-if="previewData.relatableUnits?.length" class="mb-6">
					<div class="flex flex-wrap items-center gap-3 mb-2">
						<h3 class="text-sm font-semibold text-gray-700">Relatable Impact</h3>
						<em class="text-xs text-blue-500 bg-blue-100 rounded-full px-2 py-0.5">{{ distributionIncludedNote }}</em>
					</div>
					<DataTable :value="relatableUnitsRows" size="small" stripedRows class="preview-datatable">
						<Column field="emissionFactorName">
							<template #header><I2Label label="Impact Category" helpElementName="AssessmentTabDashboardImpactCategory" /></template>
							<template #body="{ data }">
								<span class="font-medium">{{ data.emissionFactorName }}</span>
							</template>
						</Column>
						<Column field="referenceUnitLabel">
							<template #header><I2Label label="Reference Unit" helpElementName="AssessmentTabDashboardReferenceUnit" /></template>
							<template #body="{ data }">
								<span class="flex items-center gap-2">
									<FontAwesomeIcon v-if="data.iconName" :icon="['fas', data.iconName]" class="text-gray-600" />
									{{ data.referenceUnitLabel }}
								</span>
							</template>
						</Column>
						<Column field="val_0">
							<template #header>
								<span class="block w-full text-right">{{ previewData.baselineLabel || "Baseline" }}</span>
							</template>
							<template #body="{ data }">
								<span :class="{ 'font-bold': data.showBestMarker?.[0] }" class="flex items-center justify-end gap-1">
									<span v-if="data.showBestMarker?.[0]">&#9733;</span>
									<span>{{ formatNumber(data.val_0) }} {{ data.referenceUnitLabel }}</span>
								</span>
							</template>
						</Column>
						<Column v-for="(alt, idx) in previewData.alternatives" :key="'ru-d-' + idx">
							<template #header>
								<span class="block w-full text-right">Difference {{ alt.label }}</span>
							</template>
							<template #body="{ data }">
								<span :class="{ 'font-bold': data.showBestMarker?.[idx + 1] }" class="flex items-center justify-end gap-1">
									<span v-if="data.showBestMarker?.[idx + 1]">&#9733;</span>
									<span>{{ formatNumber(data[`diff_${idx + 1}`]) }} {{ data.referenceUnitLabel }}</span>
									<span>{{ relatableUnitIcon(data[`val_${idx + 1}`], data.val_0) }}</span>
								</span>
							</template>
						</Column>
					</DataTable>
				</div>

				<!-- Row 3: Impact Category Comparison -->
				<div v-if="previewData.impactCategoryComparison?.length" class="mb-6">
					<div class="flex flex-wrap items-center gap-3 mb-2">
						<h3 class="text-sm font-semibold text-gray-700">Impact Category Comparison</h3>
						<em class="text-xs text-blue-500 bg-blue-100 rounded-full px-2 py-0.5">{{ distributionIncludedNote }}</em>
					</div>
					<DataTable :value="impactCategoryRows" size="small" class="preview-datatable">
						<Column field="categoryName">
							<template #header><I2Label label="Category" helpElementName="AssessmentTabDashboardCategory" /></template>
							<template #body="{ data }">
								<span class="font-medium">{{ data.categoryName }}</span>
							</template>
						</Column>
						<Column field="unit">
							<template #header><I2Label label="Unit" helpElementName="AssessmentTabDashboardUnit" /></template>
						</Column>
						<Column>
							<template #header>
								<span class="block w-full text-right">
									Baseline<br />
									<span class="font-normal text-xs"
										>({{ dashboardConfigSummary[0]?.amount }} · {{ dashboardConfigSummary[0]?.packagingOption }} ·
										{{ dashboardConfigSummary[0]?.scaling }})</span
									>
								</span>
							</template>
							<template #body="{ data }">
								<span class="block w-full text-right"
									><span v-if="data.showBestMarker?.[0]">&#9733; </span>{{ formatNumber(data.baselineValue) }}</span
								>
							</template>
						</Column>
						<template v-for="(alt, idx) in previewData.alternatives" :key="'ic-col-' + idx">
							<Column>
								<template #header>
									<span class="block w-full text-right">
										{{ alt.label }}<br />
										<span class="font-normal text-xs"
											>({{ dashboardConfigSummary[idx + 1]?.amount }} · {{ dashboardConfigSummary[idx + 1]?.packagingOption }} ·
											{{ dashboardConfigSummary[idx + 1]?.scaling }})</span
										>
									</span>
								</template>
								<template #body="{ data }">
									<span class="block text-right">
										<span v-if="data.showBestMarker?.[idx + 1]">&#9733; </span>{{ formatNumber(data[`alt_${idx}_value`]) }}
									</span>
								</template>
							</Column>
							<Column>
								<template #header><span class="block w-full text-right">Difference</span></template>
								<template #body="{ data }">
									<span :class="`${cellStateClass(data[`alt_${idx}_cellState`])} block w-full text-right`">{{
										formatNumber(data[`alt_${idx}_diff`])
									}}</span>
								</template>
							</Column>
							<Column>
								<template #header><span class="block w-full text-right">%</span></template>
								<template #body="{ data }">
									<span :class="`${cellStateClass(data[`alt_${idx}_cellState`])} flex w-full items-center justify-end gap-1`">
										<span>{{ formatPercent(data[`alt_${idx}_pct`]) }}</span>
										<span>{{ pctIcon(data[`alt_${idx}_pct`]) }}</span>
									</span>
								</template>
							</Column>
						</template>
					</DataTable>
				</div>

				<!-- Row 4: Assessment Pallet Configuration -->
				<div v-if="previewData.packagingEfficiencyComparison?.length" class="mb-6">
					<div class="flex flex-wrap items-center gap-3 mb-2">
						<h3 class="text-sm font-semibold text-gray-700">Assessment Pallet Configuration</h3>
						<em class="text-xs text-blue-500 bg-blue-100 rounded-full px-2 py-0.5">{{ distributionExcludedNote }}</em>
					</div>
					<DataTable :value="packagingEfficiencyRows" size="small" class="preview-datatable">
						<Column field="metricName">
							<template #header><I2Label label="Metric" helpElementName="AssessmentTabDashboardMetric" /></template>
							<template #body="{ data }">
								<span class="font-medium">{{ data.metricName }}</span>
							</template>
						</Column>
						<Column>
							<template #header>
								<span class="block w-full text-right">
									Baseline<br />
									<span class="font-normal text-xs"
										>({{ dashboardConfigSummary[0]?.amount }} · {{ dashboardConfigSummary[0]?.packagingOption }} ·
										{{ dashboardConfigSummary[0]?.scaling }})</span
									>
								</span>
							</template>
							<template #body="{ data }">
								<span :class="{ 'font-bold': data.showBestMarker?.[0] }" class="block text-right">
									<span v-if="data.showBestMarker?.[0]">&#9733; </span>{{ formatNumber(data.val_0) }}
								</span>
							</template>
						</Column>
						<Column v-for="(alt, idx) in previewData.alternatives" :key="'pe-h-' + idx">
							<template #header>
								<span class="block w-full text-right">
									{{ alt.label }}<br />
									<span class="font-normal text-xs"
										>({{ dashboardConfigSummary[idx + 1]?.amount }} · {{ dashboardConfigSummary[idx + 1]?.packagingOption }} ·
										{{ dashboardConfigSummary[idx + 1]?.scaling }})</span
									>
								</span>
							</template>
							<template #body="{ data }">
								<span class="block text-right">
									<span v-if="data.showBestMarker?.[idx + 1]">&#9733; </span>{{ formatNumber(data[`val_${idx + 1}`]) }}
								</span>
							</template>
						</Column>
					</DataTable>
				</div>

				<!-- Row 5: Lifecycle Sections (chart on left, table on right — like PPTX) -->
				<div v-if="previewData.lifecycleSections?.length">
					<div v-for="(section, sIdx) in previewData.lifecycleSections" :key="'lcs-' + sIdx" class="mb-6 border border-gray-200 rounded-lg p-4">
						<div class="flex flex-wrap items-center gap-3 mb-3">
							<h3 class="text-sm font-semibold text-gray-700">{{ section.title }}</h3>
							<em class="text-xs text-blue-500 bg-blue-100 rounded-full px-2 py-0.5">{{ lifecycleSectionDistributionNote(section.title) }}</em>
						</div>
						<Accordion :value="['table']" multiple>
							<AccordionPanel value="chart">
								<AccordionHeader>Chart</AccordionHeader>
								<AccordionContent>
									<Chart
										type="bar"
										:data="buildLifecycleChartData(section)"
										:options="buildLifecycleChartOptions(section)"
										:plugins="buildLifecycleChartPlugins(section)"
									/>
								</AccordionContent>
							</AccordionPanel>
							<AccordionPanel value="table">
								<AccordionHeader>Table</AccordionHeader>
								<AccordionContent>
									<div class="overflow-auto">
										<DataTable v-if="section.comparisonRows?.length" :value="buildLifecycleTableRows(section)" size="small" class="preview-datatable">
											<Column field="categoryName">
												<template #header><I2Label label="Category" helpElementName="AssessmentTabDashboardCategory" /></template>
												<template #body="{ data }">
													<span v-if="data.isSubRow" class="block pl-8 font-normal">{{ data.categoryName }}</span>
													<strong v-else-if="data.hasSubRows">{{ data.categoryName }}</strong>
													<span v-else class="font-medium">{{ data.categoryName }}</span>
												</template>
											</Column>
											<Column>
												<template #header><I2Label label="Unit" helpElementName="AssessmentTabDashboardUnit" /></template>
												<template #body>
													<span class="text-xs">{{ section.unit }}</span>
												</template>
											</Column>
											<Column>
												<template #header>
													<span class="block w-full text-right">
														Baseline<br />
														<span class="font-normal text-xs"
															>({{ dashboardConfigSummary[0]?.amount }} · {{ dashboardConfigSummary[0]?.packagingOption }} ·
															{{ dashboardConfigSummary[0]?.scaling }})</span
														>
													</span>
												</template>
												<template #body="{ data }">
													<span :class="{ 'font-bold': data.showBestMarker?.[0] }" class="block text-right">
														<span v-if="data.showBestMarker?.[0]">&#9733; </span>{{ formatNumber(data.baselineValue) }}
													</span>
												</template>
											</Column>
											<template v-for="(alt, idx) in previewData.alternatives" :key="'lc-col-' + sIdx + '-' + idx">
												<Column>
													<template #header>
														<span class="block w-full text-right">
															{{ alt.label }}<br />
															<span class="font-normal text-xs"
																>({{ dashboardConfigSummary[idx + 1]?.amount }} · {{ dashboardConfigSummary[idx + 1]?.packagingOption }} ·
																{{ dashboardConfigSummary[idx + 1]?.scaling }})</span
															>
														</span>
													</template>
													<template #body="{ data }">
														<span class="block text-right">
															<span v-if="data.showBestMarker?.[idx + 1]">&#9733; </span>{{ formatNumber(data[`alt_${idx}_value`]) }}
														</span>
													</template>
												</Column>
												<Column>
													<template #header><span class="block w-full text-right">Difference</span></template>
													<template #body="{ data }">
														<span :class="`${cellStateClass(data[`alt_${idx}_cellState`])} block w-full text-right`">{{
															formatNumber(data[`alt_${idx}_diff`])
														}}</span>
													</template>
												</Column>
												<Column>
													<template #header><span class="block w-full text-right">%</span></template>
													<template #body="{ data }">
														<span :class="`${cellStateClass(data[`alt_${idx}_cellState`])} flex w-full items-center justify-end gap-1`">
															<span>{{ formatPercent(data[`alt_${idx}_pct`]) }}</span>
															<span>{{ pctIcon(data[`alt_${idx}_pct`]) }}</span>
														</span>
													</template>
												</Column>
											</template>
										</DataTable>
									</div>
								</AccordionContent>
							</AccordionPanel>
						</Accordion>
					</div>
				</div>
			</div>
		</template>

		<!-- EDITOR MODE -->
		<template v-else>
			<!-- Header -->
			<div class="flex items-center justify-between">
				<!-- Page Label with Selected Option Name -->
				<PageLabel :helpPageName="'Assessments' + props.tabName.replace(/\s+/g, '') + props.subTabName.replace(/\s+/g, '')"> Dashboard </PageLabel>

				<div class="flex gap-2">
					<Button label="Preview" icon="pi pi-eye" severity="info" :loading="saving || loadingPreview" @click="openPreview" />
					<Button
						label="Export PPT"
						icon="pi pi-download"
						severity="secondary"
						:loading="exporting"
						:disabled="!canExport"
						@click="exportPowerPoint"
					/>
					<!-- <Button
						label="Export Word"
						icon="pi pi-file-word"
						severity="secondary"
						:loading="exportingWord"

						:disabled="!canExport"
						@click="exportWord"
					/> -->
					<Button label="Export PDF" icon="pi pi-file-pdf" severity="secondary" :loading="exportingPdf" :disabled="!canExport" @click="exportPdf" />
				</div>
			</div>
			<!-- Primary Package Format (Baseline) -->
			<div class="i2Panel p-4">
				<h3 class="text-base font-semibold text-blue-800 mb-3">Baseline Format</h3>
				<!-- <h3 class="text-base font-semibold text-blue-800 mb-3">Baseline Package Format</h3> -->
				<div class="grid grid-cols-4 gap-4">
					<I2FormField label="Packaging Option" helpElementName="ReportPackagingOption">
						<Select
							v-model="config.baselinePackagingGIId"
							:options="packagingGIOptions"
							optionLabel="label"
							optionValue="value"
							placeholder="Select Format"
							:class="{ 'p-invalid': previewAttempted && !config.baselinePackagingGIId }"
						/>
					</I2FormField>
					<I2FormField label="Distribution Option" helpElementName="ReportDistributionOption">
						<div class="flex items-center gap-2">
							<span
								v-if="distributionDisabled"
								v-tooltip.right="{ value: distributionWarningTooltip, class: 'distribution-warning-tooltip' }"
								class="text-yellow-500 cursor-pointer"
							>
								<FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" />
							</span>
						</div>
						<Select
							v-model="config.baselineDistributionGIId"
							:options="distributionGIOptions"
							optionLabel="label"
							optionValue="value"
							placeholder="Select Distribution"
							class="w-full"
							showClear
							:disabled="distributionDisabled"
							:filter="distributionGIOptions.length > 10"
						/>
					</I2FormField>
					<I2FormField label="Amount" helpElementName="ReportAmount">
						<InputNumber v-model="config.baselineAmount" :min="1" />
					</I2FormField>
					<I2FormField label="Scaling Per" helpElementName="ReportScalingPer">
						<Select v-model="config.baselineScaling" :options="scalingOptions" optionLabel="label" optionValue="value" placeholder="Select Scaling" />
					</I2FormField>
				</div>
			</div>

			<!-- Reduction Focus -->
			<div class="i2Panel p-4">
				<div class="flex flex-col gap-1" style="max-width: 50%">
					<I2FormField label="Reduction Focus" helpElementName="ReportReductionFocus">
						<Select
							v-model="config.reductionFocus"
							:options="reductionFocusOptions"
							optionLabel="label"
							optionValue="value"
							placeholder="Select Reduction Focus"
						/>
					</I2FormField>
				</div>
			</div>

			<!-- Spider Chart (mirrors the Final dashboard chart — same source data) -->
			<div class="i2Panel p-4">
				<h3 class="text-base font-semibold text-blue-800 mb-3">Indicators Preview</h3>
				<div v-if="radarPreviewLoading && !previewData?.radarChart" class="flex items-center justify-center h-[360px] text-gray-400 text-sm italic">
					<i class="pi pi-spinner pi-spin mr-2"></i> Loading preview...
				</div>
				<div class="h-[360px]" v-else-if="previewData?.radarChart && previewData.radarChart.labels?.length >= 3">
					<dashboardSpiderChart
						:labels="previewData.radarChart.labels"
						:datasets="previewRadarDatasets"
						:axisMin="previewData.radarChart.axisMin"
						:axisMax="previewData.radarChart.axisMax"
						:labelMaxLength="24"
					/>
				</div>
				<div v-else class="flex items-center justify-center h-[360px] text-gray-400 text-sm italic">
					{{ previewValidationMessages.length > 0 ? previewValidationMessages[0] : "Complete the required selections to see the chart" }}
				</div>
			</div>

			<!-- Alternative Packaging Formats + Emission Factors side by side -->
			<div class="grid grid-cols-3 gap-4">
				<!-- Alternative Packaging Formats (left 2/3) -->
				<div class="i2Panel p-4 col-span-2" :class="{ 'panel-invalid': previewAttempted && config.alternatives.length === 0 }">
					<div class="flex items-center justify-between mb-3">
						<h3 class="text-base font-semibold text-blue-800">Alternative Packaging Formats</h3>
						<Button label="ADD ALTERNATIVE" icon="pi pi-plus" @click="openAddDialog" />
					</div>
					<DataTable
						:value="config.alternatives"
						size="small"
						v-if="config.alternatives.length > 0"
						reorderableRows
						@row-reorder="config.alternatives = $event.value"
					>
						<Column rowReorder style="width: 2rem" />
						<Column header="" style="width: 110px">
							<template #body="{ index }">
								<span class="text-xs font-semibold text-blue-700">Alternative {{ index + 1 }}</span>
							</template>
						</Column>
						<Column>
							<template #header><I2Label label="Format" helpElementName="AssessmentTabDashboardFormat" /></template>
							<template #body="{ data }">{{ getGILabel(data.packagingGIId, packagingGIOptions) }}</template>
						</Column>
						<Column>
							<template #header><I2Label label="Distribution" helpElementName="AssessmentTabDashboardDistribution" /></template>
							<template #body="{ data }">{{ getGILabel(data.distributionGIId, distributionGIOptions) }}</template>
						</Column>
						<Column>
							<template #header><I2Label label="Amount" helpElementName="AssessmentTabDashboardAmount" /></template>
							<template #body="{ data }">{{ data.amount }}</template>
						</Column>
						<Column>
							<template #header><I2Label label="Scaling" helpElementName="AssessmentTabDashboardScaling" /></template>
							<template #body="{ data }">{{ getGILabel(data.scaling, scalingOptions) }}</template>
						</Column>
						<Column
							header="Actions"
							headerStyle="width: 100px; text-align: center;"
							bodyStyle="width: 100px; vertical-align: middle; text-align: center;"
						>
							<template #body="{ index }">
								<div class="flex items-center justify-center gap-2">
									<Button icon="pi pi-pencil" outlined severity="info" class="icon-btn" aria-label="Edit" @click="editAlternative(index)" />
									<Button icon="pi pi-trash" outlined severity="danger" class="icon-btn" aria-label="Remove" @click="removeAlternative(index)" />
								</div>
							</template>
						</Column>
					</DataTable>
					<div v-else class="text-gray-400 text-sm italic">No alternatives added yet. Click "ADD ALTERNATIVE" to add one.</div>
				</div>

				<!-- Emission Factors (right 1/3) -->
				<div class="i2Panel p-4" :class="{ 'panel-invalid': previewAttempted && reportsStore.selectedEmissionFactorNames.length === 0 }">
					<h3 class="text-base font-semibold text-blue-800 mb-3">Emission Factors</h3>
					<SelectableList
						v-model="reportsStore.selectedEmissionFactorNames"
						:options="reportsStore.emissionFactors"
						option-label="emissionFactorName"
						option-value="emissionFactorName"
						placeholder="Select Emission Factors"
						:filter="true"
						:preserve-option-order="true"
					/>
				</div>
			</div>

			<!-- Add / Edit Alternative Dialog -->
			<dashboardAlternativeDialog
				v-model:visible="showAddDialog"
				:packagingOptions="packagingGIOptions"
				:distributionOptions="distributionGIOptions"
				:scalingOptions="scalingOptions"
				:editData="editingAlternative"
				@save="onAlternativeSaved"
			/>
		</template>

		<!-- Radar Chart Expand Dialog (outside preview container to avoid layout interference) -->
		<Dialog
			v-model:visible="showRadarDialog"
			modal
			header="Radar Chart"
			:style="{ width: 'min(90vw, 1200px)' }"
			:contentStyle="{ overflow: 'auto', maxHeight: '85vh' }"
		>
			<div v-if="previewData?.radarChart" class="radar-dialog-chart">
				<dashboardSpiderChart
					:labels="previewData.radarChart.labels"
					:datasets="previewRadarDatasets"
					:axisMin="previewData.radarChart.axisMin"
					:axisMax="previewData.radarChart.axisMax"
					:labelMaxLength="34"
				/>
			</div>
			<DataTable v-if="previewData?.radarChart" :value="radarTableRows" class="mt-4 radar-detail-table" size="small" stripedRows>
				<Column field="indicator">
					<template #header><I2Label label="Indicator" helpElementName="AssessmentTabDashboardIndicator" /></template>
				</Column>
				<Column v-for="col in radarTableColumns" :key="col" :field="col" :header="col" class="text-center" />
			</DataTable>
		</Dialog>

		<!-- PPT VIVA! Export Options Dialog -->
		<Dialog v-model:visible="showVivaDialog" modal header="PPT VIVA! — Export Options" :style="{ width: '480px' }">
			<div class="flex flex-col gap-4">
				<p class="text-sm text-gray-500">Select which sections to include in the presentation.</p>

				<!-- Dashboard -->
				<div class="flex flex-col gap-2 border border-gray-200 rounded-lg p-3">
					<div class="flex items-center gap-2">
						<Checkbox v-model="vivaOptions.includeDashboard" :binary="true" inputId="viva-dashboard" />
						<label for="viva-dashboard" class="font-semibold text-sm">Dashboard</label>
						<span class="text-xs text-gray-400">(Metadata, KPIs)</span>
					</div>
					<div class="flex items-center gap-2 ml-6" :class="{ 'opacity-40': !vivaOptions.includeDashboard }">
						<Checkbox v-model="vivaOptions.includeRadarChart" :binary="true" :disabled="!vivaOptions.includeDashboard" inputId="viva-radar" />
						<label for="viva-radar" class="text-sm">Radar Chart</label>
					</div>
				</div>

				<!-- Relatable Impact -->
				<div class="flex items-center gap-2 border border-gray-200 rounded-lg p-3">
					<Checkbox v-model="vivaOptions.includeRelatableImpact" :binary="true" inputId="viva-relatable" />
					<label for="viva-relatable" class="font-semibold text-sm">Relatable Impact</label>
				</div>

				<!-- Impact Category -->
				<div class="flex items-center gap-2 border border-gray-200 rounded-lg p-3">
					<Checkbox v-model="vivaOptions.includeImpactCategory" :binary="true" inputId="viva-impact" />
					<label for="viva-impact" class="font-semibold text-sm">Impact Category Comparison</label>
				</div>

				<!-- Assessment Pallet Configuration -->
				<div class="flex items-center gap-2 border border-gray-200 rounded-lg p-3">
					<Checkbox v-model="vivaOptions.includePackagingEfficiency" :binary="true" inputId="viva-packaging" />
					<label for="viva-packaging" class="font-semibold text-sm">Assessment Pallet Configuration</label>
				</div>

				<!-- Lifecycle Sections -->
				<div class="flex flex-col gap-2 border border-gray-200 rounded-lg p-3">
					<div class="flex items-center gap-2">
						<Checkbox v-model="vivaOptions.includeLifecycleSections" :binary="true" inputId="viva-lifecycle" />
						<label for="viva-lifecycle" class="font-semibold text-sm">Lifecycle Sections</label>
						<span class="text-xs text-gray-400">(Detailed Impact Comparison)</span>
					</div>
					<div class="flex gap-6 ml-6" :class="{ 'opacity-40': !vivaOptions.includeLifecycleSections }">
						<div class="flex items-center gap-2">
							<Checkbox
								v-model="vivaOptions.includeLifecycleCharts"
								:binary="true"
								:disabled="!vivaOptions.includeLifecycleSections"
								inputId="viva-lc-charts"
							/>
							<label for="viva-lc-charts" class="text-sm">Charts</label>
						</div>
						<div class="flex items-center gap-2">
							<Checkbox
								v-model="vivaOptions.includeLifecycleTables"
								:binary="true"
								:disabled="!vivaOptions.includeLifecycleSections"
								inputId="viva-lc-tables"
							/>
							<label for="viva-lc-tables" class="text-sm">Tables</label>
						</div>
					</div>
				</div>
			</div>

			<template #footer>
				<div class="flex justify-end gap-2">
					<Button label="Cancel" severity="secondary" @click="showVivaDialog = false" />
					<Button label="Export" icon="pi pi-download" severity="warn" :loading="exportingViva" @click="exportPptViva" />
				</div>
			</template>
		</Dialog>
	</div>
</template>

<script setup lang="ts">
import I2Label from "@/crispify/crispy-components/I2Label.vue"
import { ref, computed, onMounted, watch, inject, type Ref } from "vue"
import Button from "primevue/button"
import Select from "primevue/select"
import InputNumber from "primevue/inputnumber"
import Textarea from "primevue/textarea"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import Dialog from "primevue/dialog"
import Checkbox from "primevue/checkbox"
import { valueToStringFormatter } from "@/composables/resultGridHelper"
import { ApiClient, GeneralInformation, DashboardExportClient, DashboardExportRequest, PptVivaExportRequest } from "../../entities/NSwagGenerated"
import { useReportsStore } from "@/store/reportsStore"
import { useToaster, useErrorToaster } from "@/composables/toaster"
import dashboardAlternativeDialog from "./dashboardAlternativeDialog.vue"
import dashboardSpiderChart from "./dashboardSpiderChart.vue"
import SelectableList from "./SelectableList.vue"
import Chart from "primevue/chart"
import Accordion from "primevue/accordion"
import AccordionPanel from "primevue/accordionpanel"
import AccordionHeader from "primevue/accordionheader"
import AccordionContent from "primevue/accordioncontent"
import { EventBus } from "@/eventBus"
import warningBlock from "./warningBlock.vue"

// Screen IDs - same constants used in lifeCycleSummaryResult.vue
const DISTRIBUTION_SCREEN_ID = "a69c4ff1-1508-46de-9c33-2247716da798"

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	subTabName: {
		type: String,
		required: false,
		default: "",
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: () => ({}),
	},
})

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const exportClient = new DashboardExportClient(import.meta.env.VITE_API_URL?.toString())
const reportsStore = useReportsStore()

// ---------------------------------------------------------------------------
// Validation state
// ---------------------------------------------------------------------------

interface OptionValidationResult {
	optionId: string
	optionName: string
	messages: string[]
}
const optionValidationResults = ref<OptionValidationResult[]>([])

const failingOptionIds = computed(() => new Set(optionValidationResults.value.map((r) => r.optionId)))

const flattenedValidationMessages = computed(() => [
	`<span style="display:block; font-style:italic; color:#6b7280; font-size:0.875rem; margin-bottom:4px;">Packaging formats with validation errors cannot be used for calculation. Please fix the issues for them if you want to use them for Dashboard.</span>`,
	...optionValidationResults.value.flatMap((result) => [
		`<span style="display:block; font-weight:600; font-size:0.9rem; color:#92400e; border-left:2px solid #d97706; padding-left:6px; margin-top:4px;">${result.optionName}</span>`,
		...result.messages.map((msg) => `<span style="display:block; padding-left:16px;">${msg}</span>`),
	]),
])
const displayValidationRunning = ref(false)
const rawPackagingGIOptions = ref<any[]>([])

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SelectOption {
	label: string
	value: string
}

interface AlternativeEntry {
	id?: string
	packagingGIId: string | null
	distributionGIId: string | null
	amount: number | null
	scaling: string | null
	sortOrder?: number
}

interface DashboardConfig {
	description: string
	baselinePackagingGIId: string | null
	baselineDistributionGIId: string | null
	baselineAmount: number | null
	baselineScaling: string | null
	reductionFocus: string | null
	alternatives: AlternativeEntry[]
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const saving = ref(false)
const loading = ref(false)
const exporting = ref(false)
const exportingWord = ref(false)
const exportingPdf = ref(false)
const exportingViva = ref(false)
const isDirty = ref(false)
const configId = ref<string | undefined>(undefined)
const showPreview = ref(false)
const showRadarDialog = ref(false)
const showVivaDialog = ref(false)
const loadingPreview = ref(false)
const radarPreviewLoading = ref(false)
const previewAttempted = ref(false)
const showPreviewValidationWarning = ref(false)

const vivaOptions = ref({
	includeDashboard: true,
	includeRadarChart: true,
	includeRelatableImpact: true,
	includeImpactCategory: true,
	includePackagingEfficiency: true,
	includeLifecycleSections: true,
	includeLifecycleCharts: true,
	includeLifecycleTables: true,
})

// Preview data types (matching API DTOs - will be replaced by NSwag types after regen)
interface PreviewAlternativeDto {
	label: string
	formatName: string
}
interface PreviewReductionHighlightDto {
	label: string
	icon: string
	value: number
	bestAlternativeLabel: string
}
interface PreviewRadarSeriesDto {
	label: string
	normalizedValues: number[]
}
interface PreviewRadarChartDto {
	labels: string[]
	series: PreviewRadarSeriesDto[]
	axisMin: number
	axisMax: number
}
interface PreviewImpactCategoryRowDto {
	categoryName: string
	unit: string
	values: number[]
	diffFromBaseline: number[]
	percentDiff: number[]
	isBest: boolean[]
	cellState: string[]
	lowerIsBetter: boolean
	isSubRow: boolean
}
interface PreviewPackagingEfficiencyRowDto {
	metricName: string
	values: number[]
	isBest: boolean[]
}
interface PreviewRelatableUnitRowDto {
	emissionFactorName: string
	referenceActivity: string
	referenceUnitLabel: string
	iconName: string
	convertedValues: number[]
	diffFromBaseline: number[]
	isBest: boolean[]
}
interface PreviewLifecycleSectionDto {
	title: string
	unit: string
	categoryLabels: string[]
	optionLabels: string[]
	values: number[][]
	comparisonRows: PreviewImpactCategoryRowDto[]
}
interface PreviewDataType {
	assessmentName: string
	contactPerson: string
	description: string
	date: string
	baselineLabel: string
	baselineFormatName: string
	alternatives: PreviewAlternativeDto[]
	reductionHighlights: PreviewReductionHighlightDto[]
	radarChart: PreviewRadarChartDto | null
	impactCategoryComparison: PreviewImpactCategoryRowDto[]
	packagingEfficiencyComparison: PreviewPackagingEfficiencyRowDto[]
	relatableUnits: PreviewRelatableUnitRowDto[]
	lifecycleSections: PreviewLifecycleSectionDto[]
}

const previewData = ref<PreviewDataType | null>(null)

const config = ref<DashboardConfig>({
	description: "",
	baselinePackagingGIId: null,
	baselineDistributionGIId: null,
	baselineAmount: null,
	baselineScaling: null,
	reductionFocus: null,
	alternatives: [],
})

// Snapshot used to detect dirty state
const savedSnapshot = ref<string>("")

// Injected from commonTabs — stays in sync automatically when packaging options are added/removed
const injectedPackagingOptions = inject<Ref<GeneralInformation[]>>("packagingOptions", ref([]))
const packagingGIOptions = computed<SelectOption[]>(() =>
	injectedPackagingOptions.value
		.filter((gi) => !failingOptionIds.value.has(gi.id || ""))
		.map((gi) => ({
			label: gi.generalInformationName || gi.versionName || gi.id || "",
			value: gi.id || "",
		}))
)
const distributionGIOptions = ref<SelectOption[]>([])
const hasDistributionScreen = ref<boolean>(false)

const distributionNotMandatoryNote =
	"\n\nThis field is not mandatory: you can proceed with the calculation even if no Distribution Option is selected."

const distributionWarningTooltipNoScreen =
	'If you want a Distribution scenario to be considered in the Device & Packaging dashboard, the assessment should combine both "Device & Packaging" and "Distribution" modules. The respective distribution scenarios should then be created through the Distribution module (Distribution tab). If you are not able to see the Distribution tab, then you need to contact the administrators to enable it for your assessment.\n\nNote: The distribution tab can currently be enabled by editing the Sub Project. The sub project can be edited by admin users' +
	distributionNotMandatoryNote

const distributionWarningTooltipNoOptions =
	"There are no distribution options available to add to the calculation. Please create a distribution scenario in the Distribution module (Distribution tab) first." +
	distributionNotMandatoryNote

const distributionDisabled = computed(() => !hasDistributionScreen.value || distributionGIOptions.value.length === 0)

const distributionWarningTooltip = computed(() => {
	if (!hasDistributionScreen.value) return distributionWarningTooltipNoScreen
	if (distributionGIOptions.value.length === 0) return distributionWarningTooltipNoOptions
	return ""
})

// Dialog state
const showAddDialog = ref(false)
const editingAlternative = ref<AlternativeEntry | null>(null)
const editingAlternativeIndex = ref<number>(-1)

// ---------------------------------------------------------------------------
// Static option lists
// ---------------------------------------------------------------------------

const scalingOptions: SelectOption[] = [
	{ label: "Reference Unit", value: "Per Reference Unit" },
	{ label: "Primary Pack", value: "Per Primary Pack" },
	{ label: "Secondary Pack", value: "Per Secondary Pack" },
	{ label: "Pallet", value: "Per Pallet" },
]

const reductionFocusOptions: SelectOption[] = [
	{ label: "Climate Change (kg CO2e)", value: "Climate Change (kg CO2e)" },
	{ label: "Freshwater consumption (m3)", value: "Water (m3)" },
	{ label: "Packaging Mass (g)", value: "Packaging Mass (g)" },
	{ label: "Total Weighted Eco-score (mPt)", value: "Total Weighted Eco-score (mPt)" },
	{ label: "Plastic Reduction", value: "Plastic Reduction" },
	{ label: "Paper Reduction", value: "Paper Reduction" },
	{ label: "Primary Pack per Shipper", value: "Primary Pack per Shipper" },
]

// Fixed spider diagram indicators — same concepts as Reduction Focus but without units
const spiderIndicatorOptions: SelectOption[] = [
	{ label: "Climate change", value: "Climate change" },
	{ label: "Water", value: "Water" },
	{ label: "Packaging mass", value: "Packaging mass" },
	{ label: "Total weighted eco-score", value: "Total weighted eco-score" },
	{ label: "Plastic mass", value: "Plastic mass" },
	{ label: "Paper mass", value: "Paper mass" },
	{ label: "Primary pack per shipper", value: "Primary pack per shipper" },
]

// ---------------------------------------------------------------------------
// Dirty detection
// ---------------------------------------------------------------------------

function buildConfigSnapshot(): string {
	return JSON.stringify({
		config: config.value,
		selectedEmissionFactorNames: [...reportsStore.selectedEmissionFactorNames].sort(),
	})
}

watch(
	[config, () => reportsStore.selectedEmissionFactorNames],
	() => {
		isDirty.value = buildConfigSnapshot() !== savedSnapshot.value
	},
	{ deep: true }
)

// ---------------------------------------------------------------------------
// Export readiness
// ---------------------------------------------------------------------------

const canExport = computed(() => {
	return (
		!!configId.value &&
		!!config.value.baselinePackagingGIId &&
		config.value.alternatives.length > 0 &&
		reportsStore.selectedEmissionFactorNames.length > 0 &&
		!isDirty.value &&
		!saving.value
	)
})

const previewValidationMessages = computed<string[]>(() => {
	const messages: string[] = []
	if (!config.value.baselinePackagingGIId) {
		messages.push("Select a Baseline Packaging Option.")
	}
	if (config.value.alternatives.length === 0) {
		messages.push("Add at least one Alternative Packaging Format.")
	}
	if (reportsStore.selectedEmissionFactorNames.length === 0) {
		messages.push("Select at least one Emission Factor.")
	}
	return messages
})

// Auto-dismiss the validation warning once all required fields are filled in
watch(previewValidationMessages, (messages) => {
	if (messages.length === 0) {
		showPreviewValidationWarning.value = false
	}
})

// ---------------------------------------------------------------------------
// Spider chart preview data
// ---------------------------------------------------------------------------

const CHART_COLORS = ["#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#5B9BD5", "#70AD47"]
const CHART_LEGEND_FONT_SIZE = 14

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function loadDistributionGIOptions() {
	if (!props.assessmentId) return

	try {
		const result = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, DISTRIBUTION_SCREEN_ID)
		distributionGIOptions.value = result
			.filter((gi) => gi.isActive === true)
			.map((gi) => ({
				label: gi.generalInformationName || gi.versionName || gi.id || "",
				value: gi.id || "",
			}))
			.sort((a, b) => a.label.localeCompare(b.label))
	} catch (error) {
		console.error("Error loading distribution GI options:", error)
		distributionGIOptions.value = []
	}
}

async function loadHasDistributionScreen() {
	if (!props.assessmentId) {
		hasDistributionScreen.value = false
		return
	}

	try {
		const screens = await apiClient.subProjectSelectedScreens(props.assessmentId)
		hasDistributionScreen.value = screens.some((s: any) => s.screenName === "Distribution")
	} catch (error) {
		console.error("Error loading subProject screens:", error)
		hasDistributionScreen.value = false
	}
}

async function loadConfig() {
	if (!props.assessmentId) return

	loading.value = true
	try {
		const result = await apiClient.dashboardConfigurationGet(props.assessmentId)
		if (result && result.id) {
			configId.value = result.id
			config.value.description = result.description || ""
			config.value.baselinePackagingGIId = result.baselinePackagingGIId || null
			config.value.baselineDistributionGIId = result.baselineDistributionGIId || null
			config.value.baselineAmount = result.baselineAmount ?? null
			config.value.baselineScaling = result.baselineScaling || null
			config.value.reductionFocus = result.reductionFocus || null

			// Unified indicator selection → emission factor name list in the store
			const savedEfIds = ((result as any).selectedIndicators || []).map((i: any) => i.emissionFactorId).filter(Boolean)
			const savedEfNames = savedEfIds
				.map((id: string) => reportsStore.emissionFactors.find((ef) => ef.id === id)?.emissionFactorName)
				.filter(Boolean) as string[]
			if (savedEfNames.length > 0) {
				reportsStore.selectedEmissionFactorNames = savedEfNames
			}

			config.value.alternatives = (result.alternatives || [])
				.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
				.map((alt) => ({
					id: alt.id,
					packagingGIId: alt.packagingGIId || null,
					distributionGIId: alt.distributionGIId || null,
					amount: alt.amount ?? null,
					scaling: alt.scaling || null,
					sortOrder: alt.sortOrder,
				}))
		}
	} catch (error) {
		// 404 or empty result means no config exists yet — form starts empty
		console.info("No existing dashboard configuration found for this assessment.")
	} finally {
		loading.value = false
		savedSnapshot.value = buildConfigSnapshot()
		isDirty.value = false
	}
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

async function saveConfig(silent = false) {
	if (!config.value.baselinePackagingGIId) {
		useToaster({
			summary: "Validation",
			detail: "Please select a baseline packaging format before saving.",
			severity: "warn",
			life: 3000,
		})
		return
	}

	saving.value = true
	try {
		// Plain object payload: the NSwag-generated DashboardConfigurationDto.toJSON() does not yet know about
		// `selectedIndicators` (the unified list) — using fetch bypasses that serializer until TS is regenerated.
		const payload = {
			id: configId.value,
			assessmentId: props.assessmentId,
			description: config.value.description || undefined,
			baselinePackagingGIId: config.value.baselinePackagingGIId || undefined,
			baselineDistributionGIId: config.value.baselineDistributionGIId || undefined,
			baselineAmount: config.value.baselineAmount ?? undefined,
			baselineScaling: config.value.baselineScaling || undefined,
			reductionFocus: config.value.reductionFocus || undefined,
			alternatives: config.value.alternatives.map((alt, index) => ({
				id: alt.id,
				packagingGIId: alt.packagingGIId || undefined,
				distributionGIId: alt.distributionGIId || undefined,
				amount: alt.amount ?? undefined,
				scaling: alt.scaling || undefined,
				sortOrder: index,
			})),
			selectedIndicators: reportsStore.selectedEmissionFactorNames
				.map((name) => {
					const ef = reportsStore.emissionFactors.find((e) => e.emissionFactorName === name)
					return ef?.id ? { emissionFactorId: ef.id } : null
				})
				.filter(Boolean),
		}

		const apiUrl = import.meta.env.VITE_API_URL?.toString() || ""
		const token = localStorage.getItem("token") || ""
		const response = await fetch(`${apiUrl}/api/dashboardConfiguration`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const errBody = await response.json().catch(() => null)
			throw new Error(errBody?.message || `Save failed with status ${response.status}`)
		}

		const result = await response.json()
		if (result && result.id) {
			configId.value = result.id
		}

		if (!silent) {
			useToaster({
				summary: "Saved",
				detail: "Dashboard configuration saved successfully.",
				severity: "success",
				life: 3000,
			})
		}
		savedSnapshot.value = buildConfigSnapshot()
		isDirty.value = false
	} catch (error) {
		useErrorToaster(error)
	} finally {
		saving.value = false
	}
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

async function exportPowerPoint() {
	if (!configId.value) return
	exporting.value = true
	try {
		const request = new DashboardExportRequest({
			dashboardConfigurationId: configId.value,
		})
		const response = await exportClient.powerpoint(request)

		// Download file - response is a FileResponse with data: Blob
		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL
		const fileName = response.fileName || `Packaging_Dashboard_${Date.now()}.pptx`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()

		// Cleanup
		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		useToaster({
			severity: "success",
			summary: "Success",
			detail: "Dashboard exported successfully",
			life: 3000,
		})
	} catch (error) {
		console.error("Export error:", error)
		useErrorToaster(error)
	} finally {
		exporting.value = false
	}
}

async function exportWord() {
	if (!configId.value) return
	exportingWord.value = true
	try {
		const request = new DashboardExportRequest({
			dashboardConfigurationId: configId.value,
		})
		// WordExportRequest has the same shape as PowerPointExportRequest;
		// after NSwag regen, swap to: const request = new WordExportRequest(...)
		const response = await exportClient.word(request as any)

		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL
		const fileName = response.fileName || `Packaging_Dashboard_${Date.now()}.docx`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()

		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		useToaster({
			severity: "success",
			summary: "Success",
			detail: "Word dashboard exported successfully",
			life: 3000,
		})
	} catch (error) {
		console.error("Word export error:", error)
		useErrorToaster(error)
	} finally {
		exportingWord.value = false
	}
}

async function exportPdf() {
	if (!configId.value) return
	exportingPdf.value = true
	try {
		const request = new DashboardExportRequest({
			dashboardConfigurationId: configId.value,
		})
		const response = await exportClient.pdf(request)

		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL
		const fileName = response.fileName || `Packaging_Dashboard_${Date.now()}.pdf`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()

		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		useToaster({
			severity: "success",
			summary: "Success",
			detail: "PDF dashboard exported successfully",
			life: 3000,
		})
	} catch (error: any) {
		// NSwag blob endpoint: the API JSON error body arrives as a Blob in error.response
		const blobCandidate = error?.response instanceof Blob ? error.response : error?.result instanceof Blob ? error.result : null
		if (blobCandidate) {
			try {
				const text = await blobCandidate.text()
				const parsed = JSON.parse(text)
				console.error("PDF export API error:", parsed)
				useErrorToaster(parsed)
			} catch {
				console.error("PDF export error:", error)
				useErrorToaster(error)
			}
		} else {
			console.error("PDF export error:", error)
			useErrorToaster(error)
		}
	} finally {
		exportingPdf.value = false
	}
}

async function exportPptViva() {
	if (!configId.value) return
	exportingViva.value = true
	showVivaDialog.value = false
	try {
		const request = new PptVivaExportRequest({
			dashboardConfigurationId: configId.value,
			includeDashboard: vivaOptions.value.includeDashboard,
			includeRadarChart: vivaOptions.value.includeRadarChart,
			includeRelatableImpact: vivaOptions.value.includeRelatableImpact,
			includeImpactCategory: vivaOptions.value.includeImpactCategory,
			includePackagingEfficiency: vivaOptions.value.includePackagingEfficiency,
			includeLifecycleSections: vivaOptions.value.includeLifecycleSections,
			includeLifecycleCharts: vivaOptions.value.includeLifecycleCharts,
			includeLifecycleTables: vivaOptions.value.includeLifecycleTables,
		})
		const response = await exportClient.powerpointViva(request)

		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL
		const fileName = response.fileName || `Packaging_Dashboad_Viva_${Date.now()}.pptx`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()

		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		useToaster({
			severity: "success",
			summary: "Success",
			detail: "PPT VIVA! dashboard exported successfully",
			life: 3000,
		})
	} catch (error) {
		console.error("PPT VIVA export error:", error)
		useErrorToaster(error)
	} finally {
		exportingViva.value = false
	}
}

// ---------------------------------------------------------------------------
// Alternative management
// ---------------------------------------------------------------------------

function openAddDialog() {
	editingAlternative.value = null
	editingAlternativeIndex.value = -1
	showAddDialog.value = true
}

function editAlternative(index: number) {
	editingAlternative.value = { ...config.value.alternatives[index] }
	editingAlternativeIndex.value = index
	showAddDialog.value = true
}

function removeAlternative(index: number) {
	config.value.alternatives.splice(index, 1)
}

function onAlternativeSaved(data: AlternativeEntry) {
	if (editingAlternativeIndex.value >= 0) {
		// Edit mode - replace the existing entry
		config.value.alternatives.splice(editingAlternativeIndex.value, 1, { ...data })
	} else {
		// Add mode - append new entry
		config.value.alternatives.push({ ...data })
	}
	editingAlternative.value = null
	editingAlternativeIndex.value = -1
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

// Dashboard configuration summary — baseline + alternatives for the preview table
const dashboardConfigSummary = computed(() => {
	const rows = [
		{
			label: "Baseline",
			packagingOption: getGILabel(config.value.baselinePackagingGIId, packagingGIOptions.value),
			distributionOption: getGILabel(config.value.baselineDistributionGIId, distributionGIOptions.value),
			amount: config.value.baselineAmount ?? "-",
			scaling: getGILabel(config.value.baselineScaling, scalingOptions),
			reductionFocus: getGILabel(config.value.reductionFocus, reductionFocusOptions),
		},
		...config.value.alternatives.map((alt, idx) => ({
			label: `Alternative ${idx + 1}`,
			packagingOption: getGILabel(alt.packagingGIId, packagingGIOptions.value),
			distributionOption: getGILabel(alt.distributionGIId, distributionGIOptions.value),
			amount: alt.amount ?? "-",
			scaling: getGILabel(alt.scaling, scalingOptions),
			reductionFocus: "-",
		})),
	]
	return rows
})

const LIFECYCLE_SECTION_WITH_DISTRIBUTION_TITLE = "Climate Change Impact per Life Cycle Stage"

const distributionIncludedNote = "Includes distribution impact (if configured)"
const distributionExcludedNote = "Excludes distribution impact"

function lifecycleSectionDistributionNote(title: string): string {
	return title === LIFECYCLE_SECTION_WITH_DISTRIBUTION_TITLE ? distributionIncludedNote : distributionExcludedNote
}

// Relatable Units table data — only show categories present in impactCategoryComparison
const relatableUnitsRows = computed(() => {
	const pd = previewData.value
	if (!pd?.relatableUnits?.length) return []
	// Backend (DashboardComparisonService.BuildRelatableUnitRows) already filters rows
	// by the distinct referenceActivity values in the relatableUnit DB table.
	return pd.relatableUnits.map((ru: any) => {
		const row: Record<string, any> = {
			emissionFactorName: ru.emissionFactorName,
			referenceActivity: ru.referenceActivity,
			referenceUnitLabel: ru.referenceUnitLabel,
			showBestMarker: ru.isBest,
			iconName: ru.iconName,
		}
		ru.convertedValues.forEach((val: number, vIdx: number) => {
			row[`val_${vIdx}`] = val
		})
		ru.diffFromBaseline.forEach((diff: number, vIdx: number) => {
			row[`diff_${vIdx}`] = Math.abs(diff)
		})
		return row
	})
})

// Impact Category Comparison table data
const impactCategoryRows = computed(() => {
	const pd = previewData.value
	if (!pd?.impactCategoryComparison?.length) return []
	return pd.impactCategoryComparison.map((ic: any) => {
		const row: Record<string, any> = {
			categoryName: ic.categoryName,
			unit: ic.unit,
			baselineValue: ic.values[0],
			showBestMarker: ic.isBest,
		}
		const alts = pd.alternatives || []
		alts.forEach((_: any, idx: number) => {
			row[`alt_${idx}_value`] = ic.values[idx + 1]
			row[`alt_${idx}_diff`] = ic.diffFromBaseline[idx + 1]
			row[`alt_${idx}_pct`] = ic.percentDiff[idx + 1]
			row[`alt_${idx}_baseline`] = ic.values[0]
			row[`alt_${idx}_cellState`] = ic.cellState?.[idx + 1]
		})
		return row
	})
})

// Assessment Pallet Configuration table data
const packagingEfficiencyRows = computed(() => {
	const pd = previewData.value
	if (!pd?.packagingEfficiencyComparison?.length) return []
	return pd.packagingEfficiencyComparison.map((pe: any) => {
		const row: Record<string, any> = {
			metricName: pe.metricName,
			showBestMarker: pe.isBest,
		}
		pe.values.forEach((val: number, vIdx: number) => {
			row[`val_${vIdx}`] = val
		})
		return row
	})
})

// ---------------------------------------------------------------------------
// Lifecycle section stacked bar charts
// ---------------------------------------------------------------------------

function buildLifecycleChartData(section: PreviewLifecycleSectionDto) {
	const labels = section.optionLabels.map((label, idx) => {
		const cfg = dashboardConfigSummary.value[idx]
		if (!cfg) return label
		return [label, `(${cfg.amount} · ${cfg.packagingOption} · ${cfg.scaling})`]
	})
	return {
		labels,
		datasets: section.categoryLabels.map((cat, catIdx) => ({
			label: cat,
			backgroundColor: CHART_COLORS[catIdx % CHART_COLORS.length],
			data: section.values.map((optValues) => optValues[catIdx] ?? 0),
		})),
	}
}

function buildLifecycleChartOptions(section: PreviewLifecycleSectionDto) {
	return {
		responsive: true,
		maintainAspectRatio: true,
		plugins: {
			legend: {
				position: "bottom" as const,
				labels: { boxWidth: 12, font: { size: CHART_LEGEND_FONT_SIZE } },
			},
		},
		scales: {
			x: { stacked: true },
			y: {
				stacked: true,
				ticks: {
					font: (context: any) => ({
						size: 12,
						weight: context.index === (context.chart.scales.y?.ticks?.length ?? 0) - 1 ? "bold" : "normal",
					}),
					callback: (value: any, index: number, ticks: any[]) => {
						if (index === ticks.length - 1 && section.unit) return [section.unit, "", value]
						return value
					},
				},
			},
		},
	}
}

function buildLifecycleChartPlugins(_section: PreviewLifecycleSectionDto) {
	return []
}

function buildLifecycleTableRows(section: PreviewLifecycleSectionDto): Record<string, any>[] {
	if (!section.comparisonRows?.length) return []
	const hasSubRows = section.comparisonRows.some((row) => row.isSubRow)
	return section.comparisonRows.map((cr) => {
		const row: Record<string, any> = {
			categoryName: cr.categoryName,
			isSubRow: cr.isSubRow,
			hasSubRows,
			baselineValue: cr.values[0],
			showBestMarker: cr.isBest,
		}
		for (let i = 1; i < cr.values.length; i++) {
			row[`alt_${i - 1}_value`] = cr.values[i]
			row[`alt_${i - 1}_diff`] = cr.diffFromBaseline?.[i] ?? null
			row[`alt_${i - 1}_pct`] = cr.percentDiff?.[i] ?? null
			row[`alt_${i - 1}_baseline`] = cr.values[0]
			row[`alt_${i - 1}_cellState`] = cr.cellState?.[i] ?? null
		}
		return row
	})
}

const previewRadarDatasets = computed(() => {
	if (!previewData.value?.radarChart?.series) return []
	return previewData.value.radarChart.series.map((s, idx) => ({
		label: s.label,
		values: s.normalizedValues,
		color: CHART_COLORS[idx % CHART_COLORS.length],
	}))
})

const radarTableRows = computed(() => {
	const rc = previewData.value?.radarChart
	if (!rc?.labels || !rc?.series) return []
	return rc.labels.map((label: string, idx: number) => {
		const row: Record<string, string | number> = { indicator: label }
		rc.series.forEach((s: { label: string; normalizedValues: number[] }) => {
			row[s.label] = s.normalizedValues[idx]?.toFixed(2) ?? "-"
		})
		return row
	})
})

const radarTableColumns = computed(() => {
	const rc = previewData.value?.radarChart
	if (!rc?.series) return []
	return rc.series.map((s: { label: string }) => s.label)
})

async function openPreview() {
	previewAttempted.value = true

	if (previewValidationMessages.value.length > 0) {
		showPreviewValidationWarning.value = true
		return
	}
	showPreviewValidationWarning.value = false

	// Auto-save any pending changes (or create the configuration on first preview)
	if (isDirty.value || !configId.value) {
		await saveConfig()
		if (isDirty.value || !configId.value) return
	}

	showPreview.value = true
	loadingPreview.value = true
	try {
		// Use fetch directly until NSwag regen adds the preview method
		const apiUrl = import.meta.env.VITE_API_URL?.toString() || ""
		const token = localStorage.getItem("token") || ""
		const response = await fetch(`${apiUrl}/api/dashboardExport/preview`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ dashboardConfigurationId: configId.value }),
		})

		if (!response.ok) {
			const errorBody = await response.json().catch(() => null)
			throw new Error(errorBody?.message || `Preview failed with status ${response.status}`)
		}

		previewData.value = await response.json()
	} catch (error) {
		console.error("Preview error:", error)
		useErrorToaster(error)
		showPreview.value = false
	} finally {
		loadingPreview.value = false
	}
}

// ---------------------------------------------------------------------------
// radar preview refresh — keeps the "Indicators Preview" panel in sync
// with the Final dashboard chart as the user edits, without a full preview reload.
// ---------------------------------------------------------------------------

let refreshInFlight = false
let refreshPending = false

async function refreshRadarPreview() {
	if (previewValidationMessages.value.length > 0) return

	if (refreshInFlight) {
		refreshPending = true
		return
	}
	refreshInFlight = true

	try {
		// Auto-save any pending changes (or create the configuration on first refresh)
		if (isDirty.value || !configId.value) {
			await saveConfig(true)
			if (isDirty.value || !configId.value) return
		}

		radarPreviewLoading.value = true
		try {
			const apiUrl = import.meta.env.VITE_API_URL?.toString() || ""
			const token = localStorage.getItem("token") || ""
			const response = await fetch(`${apiUrl}/api/dashboardExport/preview`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ dashboardConfigurationId: configId.value }),
			})

			if (!response.ok) {
				const errorBody = await response.json().catch(() => null)
				throw new Error(errorBody?.message || `Preview refresh failed with status ${response.status}`)
			}

			previewData.value = await response.json()
		} catch (error) {
			console.error("Radar preview refresh error:", error)
		} finally {
			radarPreviewLoading.value = false
		}
	} finally {
		refreshInFlight = false
		if (refreshPending) {
			refreshPending = false
			await refreshRadarPreview()
		}
	}
}

watch(
	[config, () => reportsStore.selectedEmissionFactorNames],
	() => {
		refreshRadarPreview()
	},
	{ deep: true }
)

function formatNumber(val: number | null | undefined): string {
	if (val == null || isNaN(val)) return "-"
	return valueToStringFormatter(val)
}

// Rounds to 1 decimal and normalizes -0 to 0 (JS keeps -0 as a distinct value, and
// (-0).toFixed(1) renders "-0.0" — a tiny negative value that rounds away to nothing
// must not display or act like a real decrease).
function roundedPercent(val: number): number {
	return Math.round(val * 10) / 10 + 0
}

function formatPercent(val: number | null | undefined): string {
	if (val == null || isNaN(val)) return "-"
	const rounded = roundedPercent(val)
	const sign = rounded > 0 ? "+" : ""
	return `${sign}${rounded.toFixed(1)}%`
}

function pctIcon(val: number | null | undefined): string {
	if (val == null || isNaN(val)) return "—"
	const rounded = roundedPercent(val)
	if (rounded === 0) return "—"
	return rounded > 0 ? "↑" : "↓"
}

function relatableUnitIcon(altVal: number | null | undefined, baselineVal: number | null | undefined): string {
	if (altVal == null || baselineVal == null || isNaN(altVal) || isNaN(baselineVal) || altVal === baselineVal) return "—"
	return altVal > baselineVal ? "↑" : "↓"
}

function formatReductionValue(value: number | null | undefined, icon: string): string {
	if (value == null || isNaN(value)) return "-"
	if (icon === "pallet" || icon === "shipper") {
		return Math.round(value).toLocaleString("en-US")
	}
	return `${Math.round(value)}%`
}

const kpiIconMap: Record<string, string> = {
	leaf: "leaf",
	weight: "weight-hanging",
	droplet: "droplet",
	pallet: "pallet-boxes",
	shipper: "pallet-boxes",
	"eco-score": "gauge-high",
	plastic: "bottle-water",
	paper: "file-lines",
	neutral: "hand-holding-seedling",
}

// const kpiLabelMap: Record<string, string> = {
// 	leaf: "Change in carbon footprint",
// 	droplet: "Change in water use",
// 	weight: "Change in packaging mass",
// 	"eco-score": "Change in total weighted eco-score",
// 	plastic: "Change in plastic mass",
// 	paper: "Change in paper mass",
// 	pallet: "Change in primary pack per shipper",
// }

function getKpiTooltip(rh: { icon: string; value: number; label: string }): string {
	const descriptions: Record<string, string> = {
		leaf: "Percentage reduction in CO2 equivalent emissions (Climate Change impact) of the best alternative compared to the baseline.",
		weight: "Percentage reduction in total packaging mass of the best alternative compared to the baseline.",
		droplet: "Percentage reduction in water-related environmental impact of the best alternative compared to the baseline.",
		pallet:
			"Additional primary packs that fit on a pallet with the best alternative compared to the baseline. Calculated as: (pri packs per sec pack) x (sec packs per ter pack) x (ter packs per pallet).",
		shipper:
			"Additional primary packs that fit per shipper (tertiary pack) with the best alternative compared to the baseline. Calculated as: (pri packs per sec pack) x (sec packs per ter pack).",
		"eco-score":
			"Percentage reduction in the Total Weighted Eco-score (PEF-weighted multi-indicator score) of the best alternative compared to the baseline.",
		plastic:
			"Percentage reduction in plastic packaging mass of the best alternative compared to the baseline. Read from the Packaging Mass per Material Type breakdown.",
		paper:
			"Percentage reduction in paper packaging mass of the best alternative compared to the baseline. Read from the Packaging Mass per Material Type breakdown.",
		neutral: "Percentage reduction of the selected Reduction Focus indicator of the best alternative compared to the baseline.",
	}
	const desc = descriptions[rh.icon] || ""
	if (rh.value === 0) {
		if (rh.icon === "pallet") {
			return `${desc}\n\nValue is 0: no alternative fits more packs per pallet than the baseline, or the packing hierarchy data is not configured.`
		}
		if (rh.icon === "shipper") {
			return `${desc}\n\nValue is 0: no alternative fits more packs per shipper than the baseline, or the packing hierarchy data is not configured.`
		}
		return `${desc}\n\nValue is 0%: no alternative shows an improvement over the baseline for this indicator.`
	}
	return desc
}

function cellStateClass(cellState: string | null | undefined): string {
	if (cellState === "Better") return "preview-cell-green"
	if (cellState === "Worse") return "preview-cell-orange"
	return "preview-cell-neutral"
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getGILabel(value: string | null, options: SelectOption[]): string {
	if (!value) return ""
	const match = options.find((opt) => opt.value === value)
	return match ? match.label : value
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const loadRawPackagingGIOptions = async () => {
	if (!props.assessmentId) return
	try {
		const options = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		rawPackagingGIOptions.value = options.filter((gi: any) => gi.isActive === true)
	} catch (error) {
		console.error("Error loading packaging GI options for validation:", error)
		rawPackagingGIOptions.value = []
	}
}

const validateAllPackagingOptions = async () => {
	if (!rawPackagingGIOptions.value.length) return
	displayValidationRunning.value = true
	optionValidationResults.value = []

	const results = await Promise.all(
		rawPackagingGIOptions.value.map(async (option: any) => {
			const name = option.generalInformationName || option.versionName || "Unknown Option"
			try {
				const res = await apiClient.validateResultMatrixByGeneralInformationId(option.id)
				const messages = res?.map((item: any) => item.messageText!).filter(Boolean) ?? []
				return { optionId: option.id, optionName: name, messages }
			} catch (err: any) {
				return { optionId: option.id, optionName: name, messages: [err?.response?.message || "Validation failed"] }
			}
		})
	)

	optionValidationResults.value = results.filter((r) => r.messages.length > 0)
	displayValidationRunning.value = false
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

async function registerAllSolidIcons() {
	try {
		const mod = await import("@fortawesome/pro-solid-svg-icons")
		const { library } = await import("@fortawesome/fontawesome-svg-core")
		library.add((mod as any).fas)
	} catch (e) {
		console.error("Failed to register FontAwesome Pro Solid icons", e)
	}
}

onMounted(async () => {
	await Promise.all([loadDistributionGIOptions(), loadHasDistributionScreen(), loadConfig(), registerAllSolidIcons()])
	EventBus.on("DashboardUpdate" + props.tabId, async () => {
		await loadDistributionGIOptions()
		await loadHasDistributionScreen()
	})
	EventBus.on("DashboardUpdate" + props.tabId, async () => {
		await loadDistributionGIOptions()
		await loadHasDistributionScreen()
	})
	EventBus.on("validateResult_" + props.tabId, async () => {
		await loadRawPackagingGIOptions()
		await validateAllPackagingOptions()
	})
	EventBus.on("forceResetResults_" + props.tabId, () => {
		optionValidationResults.value = []
	})
})
</script>

<style scoped>
/* Force square dimensions + centered icon for round icon-only buttons
   (PrimeVue's rounded + size="small" + icon-only combo leaves padding asymmetric). */
.icon-btn {
	width: 2rem;
	height: 2rem;
	padding: 0 !important;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}
.icon-btn :deep(.p-button-icon) {
	margin: 0;
	font-size: 0.85rem;
	line-height: 1;
}

.panel-invalid {
	border: 1px solid #e24c4c !important;
	box-shadow: 0 0 0 1px #e24c4c;
}

.preview-header-cell {
	background-color: #548235;
	color: white;
	font-weight: 600;
	padding: 6px 10px;
	border: 1px solid #4a7330;
	width: 40%;
}
.preview-value-cell {
	background-color: white;
	padding: 6px 10px;
	border: 1px solid #d1d5db;
}
.preview-table-th {
	background-color: #548235;
	color: white;
	font-weight: 600;
	padding: 6px 8px;
	border: 1px solid #4a7330;
}
.preview-table-td {
	padding: 5px 8px;
	border: 1px solid #d1d5db;
}
.preview-cell-green,
.preview-cell-orange,
.preview-cell-neutral {
	margin: -0.375rem -0.5rem;
	padding: 0.375rem 0.5rem;
}
.preview-cell-neutral {
	background-color: #ffffff;
}
.preview-cell-green {
	background-color: #dcfce7;
	color: #15803d;
	font-weight: 600;
}
.preview-cell-orange {
	background-color: #fef3c7;
	color: #92400e;
	font-weight: 500;
}
.reduction-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	min-height: 40px;
	color: #374151;
	flex-shrink: 0;
}
.reduction-fa-icon {
	font-size: 1.75rem;
	color: #548235;
}
.radar-dialog-chart {
	position: relative;
	width: 100%;
	height: min(65vh, 700px);
	min-height: 420px;
}
.radar-expand-btn {
	position: absolute;
	top: 4px;
	right: 4px;
	z-index: 100;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: 1px solid #d1d5db;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #6b7280;
	font-size: 14px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.radar-expand-btn:hover {
	background: #f3f4f6;
	color: #374151;
}
</style>

<style>
.radar-detail-table .p-datatable-thead > tr > th,
.preview-datatable .p-datatable-thead > tr > th {
	background-color: #548235 !important;
	color: white !important;
	font-weight: 600 !important;
	border-color: #4a7330 !important;
}

.distribution-warning-tooltip .p-tooltip-text {
	max-width: 500px !important;
	width: 500px !important;
	white-space: pre-line !important;
	line-height: 1.4 !important;
}
</style>
